import { ShieldCheck, LayoutDashboard, CheckCircle, LogOut, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { supabase } from '@/lib/supabase';
import { clearLocalStoragePreservingConsent } from '@/lib/cookieConsent';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface MembershipData {
  role: string;
  org_id: string | null;
}

interface RoleSelectionPageProps {
  memberships: MembershipData[];
  onRoleSelect: (role: string, orgId: string | null) => void;
}

// Role configuration with theme-aware colors
const getRoleConfig = (t: (key: string) => string) => ({
  owner: {
    label: t('roles.owner_title'),
    description: t('roles.owner_description'),
    icon: ShieldCheck,
    color: 'from-purple-500/20 to-purple-600/20 dark:from-purple-500/30 dark:to-purple-600/30',
    borderColor: 'border-purple-500/30 dark:border-purple-500/50',
    hoverColor: 'hover:border-purple-500/60 hover:bg-purple-500/10 dark:hover:border-purple-500/80 dark:hover:bg-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  coordinator: {
    label: t('roles.coordinator_title'),
    description: t('roles.coordinator_description'),
    icon: LayoutDashboard,
    color: 'from-blue-500/20 to-blue-600/20 dark:from-blue-500/30 dark:to-blue-600/30',
    borderColor: 'border-blue-500/30 dark:border-blue-500/50',
    hoverColor: 'hover:border-blue-500/60 hover:bg-blue-500/10 dark:hover:border-blue-500/80 dark:hover:bg-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  cleaner: {
    label: t('roles.cleaner_title'),
    description: t('roles.cleaner_description'),
    icon: CheckCircle,
    color: 'from-green-500/20 to-green-600/20 dark:from-green-500/30 dark:to-green-600/30',
    borderColor: 'border-green-500/30 dark:border-green-500/50',
    hoverColor: 'hover:border-green-500/60 hover:bg-green-500/10 dark:hover:border-green-500/80 dark:hover:bg-green-500/20',
    iconColor: 'text-green-600 dark:text-green-400',
  },
});

// Function to normalize role strings (case-insensitive mapping)
// Maps database values like "Owner", "Coordinator", "Cleaner" to lowercase keys
const normalizeRole = (role: string): string => {
  const normalized = role.toLowerCase().trim();
  // Map common variations to standard keys
  const roleMap: Record<string, string> = {
    'owner': 'owner',
    'właściciel': 'owner',
    'coordinator': 'coordinator',
    'koordynator': 'coordinator',
    'cleaner': 'cleaner',
    'pracownik': 'cleaner',
    'sprzątacz': 'cleaner',
  };
  return roleMap[normalized] || normalized;
};

export const RoleSelectionPage: React.FC<RoleSelectionPageProps> = ({
  memberships,
  onRoleSelect,
}) => {
  const { t } = useTranslation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const failsafeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoSelectedRef = useRef(false);
  
  // Get role config with translations
  const roleConfig = getRoleConfig(t);

  // CRITICAL: Listen for SIGNED_OUT event to clear failsafe timeout
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        console.log('[RoleSelectionPage] SIGNED_OUT detected - czyszczenie failsafe timeout');
        if (failsafeTimeoutRef.current) {
          clearTimeout(failsafeTimeoutRef.current);
          failsafeTimeoutRef.current = null;
        }
        setIsLoggingOut(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (failsafeTimeoutRef.current) {
        clearTimeout(failsafeTimeoutRef.current);
        failsafeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) {
      console.log('[RoleSelectionPage] handleLogout: już w trakcie wylogowania, pomijam');
      return;
    }

    setIsLoggingOut(true);
    console.log('[RoleSelectionPage] handleLogout: start, timestamp=', Date.now());
    
    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
      failsafeTimeoutRef.current = null;
    }
    
    try {
      console.log('[RoleSelectionPage] handleLogout: wywołuję signOut...');
      await supabase.auth.signOut();
      console.log('[RoleSelectionPage] handleLogout: signOut zakończony');
      
      console.log('[RoleSelectionPage] handleLogout: czyszczenie localStorage');
      clearLocalStoragePreservingConsent();
      
      failsafeTimeoutRef.current = setTimeout(() => {
        console.warn('[RoleSelectionPage] handleLogout: Failsafe timeout - aplikacja nie przełączyła widoku po 2s, wymuszam reload');
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.log('[RoleSelectionPage] handleLogout: CATCH error=', error);
      console.error('[RoleSelectionPage] Nieoczekiwany błąd podczas wylogowania:', error);
      
      clearLocalStoragePreservingConsent();
      
      failsafeTimeoutRef.current = setTimeout(() => {
        console.warn('[RoleSelectionPage] handleLogout: Failsafe timeout (error case) - wymuszam reload');
        window.location.reload();
      }, 2000);
    }
  };

  // DEBUG: Logowanie dostępnych ról
  console.log('DEBUG: Role dostępne w komponencie wyboru:', memberships);
  console.log('DEBUG: Oryginalne role z memberships:', memberships.map(m => m.role));
  
  // Get unique roles from memberships and normalize them
  // Normalize roles to lowercase for comparison, but keep original values for display
  const roleMap = new Map<string, string>(); // normalized -> original
  memberships.forEach(m => {
    const normalized = normalizeRole(m.role);
    if (!roleMap.has(normalized)) {
      roleMap.set(normalized, m.role); // Store first occurrence of original value
    }
  });
  
  const availableRoles = Array.from(roleMap.keys()); // Use normalized keys
  console.log('DEBUG: Znormalizowane role:', availableRoles);
  console.log('DEBUG: Mapa ról (normalized -> original):', Object.fromEntries(roleMap));

  // Auto-select if only one role is available
  useEffect(() => {
    if (availableRoles.length === 1 && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;
      const normalizedRole = availableRoles[0];
      const membership = memberships.find(m => 
        normalizeRole(m.role) === normalizedRole
      );
      
      if (membership) {
        console.log('DEBUG: Automatyczne wybranie jedynej dostępnej roli:', normalizedRole);
        onRoleSelect(normalizedRole, membership.org_id);
      }
    }
  }, [availableRoles, memberships, onRoleSelect]);

  const handleRoleClick = (normalizedRole: string) => {
    // Find the first membership with this role (case-insensitive)
    const originalRole = roleMap.get(normalizedRole);
    const membership = memberships.find(m => 
      normalizeRole(m.role) === normalizedRole
    );
    
    if (membership && originalRole) {
      console.log('DEBUG: Wybrano rolę:', {
        normalized: normalizedRole,
        original: originalRole,
        orgId: membership.org_id
      });
      
      // TYMCZASOWO: sessionStorage wyłączone dla testów
      // sessionStorage.setItem('domio-selected-role', normalizedRole);
      // sessionStorage.setItem('domio-selected-org-id', membership.org_id || '');
      
      // Use normalized role for consistency
      onRoleSelect(normalizedRole, membership.org_id);
    } else {
      console.error('DEBUG: Nie znaleziono członkostwa dla roli:', normalizedRole);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* TopBar Header */}
      <header className="w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          {/* Branding - Left Side */}
          <div className="flex items-center">
            <h1 className="text-xl font-bold">
              <span className="text-foreground">DOMIO</span>
              <span className="text-primary ml-1">Cleaning</span>
            </h1>
          </div>

          {/* Controls - Right Side */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Toggle */}
            <LanguageToggle />

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={cn(
                "h-9 w-9 flex items-center justify-center",
                "text-muted-foreground hover:text-foreground",
                "rounded-lg border border-border/50",
                "hover:border-border hover:bg-accent",
                "transition-all duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isLoggingOut && "opacity-50 cursor-not-allowed"
              )}
              aria-label={t('auth.logout')}
            >
              {isLoggingOut ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {t('auth.select_role')}
            </h1>
            <p className="text-muted-foreground text-lg">
              {t('auth.select_role_description')}
            </p>
          </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {availableRoles.map((normalizedRole) => {
            // Get config using normalized role key
            const config = roleConfig[normalizedRole as keyof typeof roleConfig];
            if (!config) {
              console.warn('DEBUG: Brak konfiguracji dla roli:', normalizedRole);
              return null;
            }

            const Icon = config.icon;
            const originalRole = roleMap.get(normalizedRole) || normalizedRole;

            return (
              <Card
                key={normalizedRole}
                className={cn(
                  "bg-card/80 dark:bg-card/90",
                  "backdrop-blur-sm",
                  "border-2",
                  config.borderColor,
                  config.hoverColor,
                  "transition-all duration-300",
                  "cursor-pointer",
                  "transform hover:scale-105",
                  "hover:shadow-lg hover:shadow-primary/20",
                  "dark:hover:shadow-primary/30",
                  "min-h-[280px]",
                  "flex flex-col",
                  "group"
                )}
                onClick={() => handleRoleClick(normalizedRole)}
              >
                <CardHeader className="flex flex-col items-center text-center pb-4">
                  <div
                    className={cn(
                      "w-16 h-16",
                      "rounded-full",
                      "bg-gradient-to-br",
                      config.color,
                      "flex items-center justify-center",
                      "mb-4",
                      "transition-transform duration-300",
                      "group-hover:scale-110",
                      "shadow-md dark:shadow-lg"
                    )}
                  >
                    <Icon className={cn("w-8 h-8", config.iconColor)} />
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {config.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <CardDescription className="text-muted-foreground text-center mb-6 flex-1">
                    {config.description}
                  </CardDescription>
                  <Button
                    className="w-full font-semibold transition-all duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleClick(normalizedRole);
                    }}
                  >
                    {t('common.select')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {availableRoles.length === 0 && (
          <div className="text-center text-muted-foreground mt-8">
            <p>{t('errors.noRolesAvailable')}</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
